<script setup lang="ts">
import { RouterLink, RouterView, useRoute } from 'vue-router'
import { computed, ref } from 'vue'
import UserMessageBanner from '@/components/UserMessageBanner.vue'
import LoginDialog from '@/components/LoginDialog.vue'
import { useAuthStore } from '@/stores/auth.store'

const route = useRoute()
const hideNav = computed(() => route.meta?.hideNav === true)
const authStore = useAuthStore()
const showLoginDialog = ref(false)

function onLogout() {
  authStore.logout()
  // 관리 페이지에 있었다면 기능테스트로 이동
  if (route.path.startsWith('/admin')) {
    window.location.href = '/test/player'
  }
}
</script>

<template>
  <header v-if="!hideNav" class="app-header">
    <div class="header-inner">
      <RouterLink to="/" class="logo">CloudEye</RouterLink>
      <nav class="nav-links">
        <span class="nav-group-label">기능테스트</span>
        <RouterLink to="/test/caster">송출</RouterLink>
        <RouterLink to="/test/player">시청</RouterLink>

        <template v-if="authStore.isLoggedIn">
          <span class="nav-divider"></span>
          <span class="nav-group-label">관리</span>
          <RouterLink to="/admin/castroom">캐스트룸</RouterLink>
          <RouterLink to="/admin/rtsps">RTSP 캐스팅</RouterLink>
          <RouterLink to="/admin/recording">녹화 영상</RouterLink>
        </template>
      </nav>

      <div class="auth-area">
        <template v-if="authStore.isLoggedIn">
          <span class="user-name">{{ authStore.name || authStore.email }}</span>
          <button class="btn-auth" @click="onLogout">로그아웃</button>
        </template>
        <template v-else>
          <button class="btn-auth btn-login" @click="showLoginDialog = true">관리자 로그인</button>
        </template>
      </div>
    </div>
  </header>

  <UserMessageBanner />
  <RouterView />

  <LoginDialog
    v-if="showLoginDialog"
    @close="showLoginDialog = false"
    @loginSuccess="showLoginDialog = false"
  />
</template>

<style lang="scss" scoped>
@use '@/assets/styles/variables' as v;

.app-header {
  background: white;
  border-bottom: 1px solid v.$color-gray-200;
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 v.$spacing-5;
  display: flex;
  align-items: center;
  height: 52px;
  gap: v.$spacing-6;
}

.logo {
  font-size: v.$font-size-lg;
  font-weight: 700;
  color: v.$color-primary;
  text-decoration: none;
  flex-shrink: 0;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: v.$spacing-1;
  overflow-x: auto;

  a {
    color: v.$color-gray-600;
    text-decoration: none;
    font-size: v.$font-size-sm;
    font-weight: 500;
    padding: 6px 10px;
    border-radius: v.$border-radius-sm;
    white-space: nowrap;
    transition: v.$transition-base;

    &:hover { background: v.$color-gray-100; }
    &.router-link-active {
      color: v.$color-primary;
      background: v.$color-blue-50;
    }
  }
}

.nav-group-label {
  font-size: 10px;
  font-weight: 700;
  color: v.$color-gray-400;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 0 4px;
}

.nav-divider {
  width: 1px;
  height: 20px;
  background: v.$color-gray-200;
  margin: 0 v.$spacing-2;
}

.auth-area {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.user-name {
  font-size: 12px;
  color: v.$color-gray-600;
}

.btn-auth {
  font-size: 12px;
  padding: 4px 10px;
  border: 1px solid v.$color-gray-300;
  border-radius: v.$border-radius-sm;
  background: white;
  color: v.$color-gray-600;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: v.$color-gray-100;
  }
}

.btn-login {
  color: v.$color-primary;
  border-color: v.$color-primary;

  &:hover {
    background: v.$color-blue-50;
  }
}
</style>
